import { useNavigate } from "react-router-dom";

import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult
} from "@tanstack/react-query";

import { UseGetAttributeValuesParams } from "@/components/dataset/datasetAttributeFilter/types";
import { Timezone } from "@/constants/dateTime";
import { MESSAGE } from "@/constants/message";
import {
  getDeepestAdminFilterFirst,
  mapDatasetFiltersToPayload
} from "@/helpers/datasetAttributeHelper";
import { removeStartFromUnderscore } from "@/helpers/string";
import useToast, { EToastType } from "@/hooks/ui/useToast";
import {
  AddUpdateRowsDatasetPayload,
  AddUpdateRowsDatasetResponse,
  AttributeOption,
  DataProviderItem,
  DatasetAnalysisPayload,
  DatasetAttributeSchema,
  DatasetAttributeValue,
  DatasetData,
  DatasetDetailResponse,
  DatasetFilter,
  DatasetFilterPayload,
  DatasetMetadata,
  DatasetMetaDataPayload,
  DatasetSchemaPayload,
  DatasetSchemas,
  DeleteRowsDatasetResponse,
  EAttributeType,
  ExportDatasetFileType,
  FilterOption,
  GetDatasetListPayload,
  GetDatasetListResponse,
  H3Configuration,
  OperationStatus,
  SpatialAggregationTypeItem,
  TranslateDatasetGeometryErrorResponse,
  TranslateDatasetGeometryPayload,
  TranslateDatasetGeometryResponse
} from "@/types/dataset";
import { CustomError } from "@/utils/errors";

import {
  addImportDataset,
  addRowsDataset,
  datasetRemoveNulls,
  deleteDataset,
  deleteRowsDataset,
  exportDataset,
  getAttributeValuesByAttributeId,
  getDataProviders,
  getDatasetByIdSchema,
  getDatasetData,
  getDatasetDetail,
  getDatasetList,
  getDatasetMetadata,
  getDatasetParsingStatus,
  getOperationStatus,
  getSpatialAggregationTypes,
  importDataset,
  patchDatasetMetadata,
  patchDatasetSchema,
  postDatasetAnalysis,
  postDatasetData,
  replaceImportDataset,
  translateDatasetGeometry,
  updateRowDataset,
  uploadDataset
} from "./datasetApi";

const removeFilterForAttribute = (
  filters: DatasetFilter[],
  attr: AttributeOption
) => {
  return filters.filter(
    (item) =>
      !(
        item.filterId === attr?.id &&
        item.attributeCode === attr?.attribute_code
      )
  );
};

export const useUploadDatasetMutation = (
  options?: UseMutationOptions<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    Error,
    FormData
  >
) => {
  return useMutation({
    mutationFn: (file: FormData) => uploadDataset(file),
    ...options
  });
};

export const useImportDatasetMutation = (
  options?: UseMutationOptions<
    { operation_id: string },
    Error,
    { file: FormData; signal?: AbortSignal }
  >
) => {
  return useMutation({
    mutationFn: ({ file, signal }: { file: FormData; signal?: AbortSignal }) =>
      importDataset(file, signal),
    ...options
  });
};

export const useAddImportDatasetMutation = (
  options?: UseMutationOptions<
    { operation_id: string },
    Error,
    { file: FormData; signal?: AbortSignal; id: string }
  >
) => {
  return useMutation({
    mutationFn: ({
      file,
      signal,
      id
    }: {
      file: FormData;
      signal?: AbortSignal;
      id: string;
    }) => addImportDataset(id, file, signal),
    ...options
  });
};

export const useReplaceImportDatasetMutation = (
  options?: UseMutationOptions<
    { operation_id: string },
    Error,
    { file: FormData; signal?: AbortSignal; id: string }
  >
) => {
  return useMutation({
    mutationFn: ({
      file,
      signal,
      id
    }: {
      file: FormData;
      signal?: AbortSignal;
      id: string;
    }) => replaceImportDataset(id, file, signal),
    ...options
  });
};

export const useExportDatasetMutation = (
  options?: UseMutationOptions<
    { operation_id: string },
    { message: CustomError },
    { id: string; fileType: ExportDatasetFileType }
  >
) => {
  return useMutation({
    mutationFn: ({ id, fileType }) => exportDataset(id, fileType),
    ...options
  });
};

export const useGetOperationStatus = (
  operationId: string,
  options?: Omit<
    UseQueryOptions<OperationStatus, Error, OperationStatus>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: ["operation-status", operationId],
    queryFn: () => getOperationStatus(operationId),
    ...options
  });
};

export const useGetDatasetParsingStatus = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: UseQueryOptions<any, Error, any>
) => {
  return useQuery({
    queryKey: ["dataset-parsing-status"],
    queryFn: () => getDatasetParsingStatus(),
    ...options
  });
};

export const useGetDatasetListQuery = (
  payload: GetDatasetListPayload,
  options?: Omit<
    UseQueryOptions<GetDatasetListResponse, Error, GetDatasetListResponse>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: ["datasets-list", payload],
    queryFn: () => getDatasetList(payload),
    ...options
  });
};

export const useGetDatasetData = (
  { id, page, per_page }: { id: string; page?: number; per_page?: number },
  options?: Omit<
    UseQueryOptions<DatasetData, Error, DatasetData>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: ["dataset-data", id, page, per_page],
    queryFn: () => getDatasetData(id, null, { page, per_page }),
    enabled: !!id,
    ...options
  });
};

type GetDatasetDataParams = {
  id: string;
  filter?: DatasetFilter[];
  h3Configuration?: H3Configuration | null;
};

/**
 * Hook to get dataset data using the POST method
 */
export const useGetDatasetDataMutation = ({
  timezone,
  options
}: {
  timezone?: Timezone;
  options?: UseMutationOptions<
    DatasetData,
    Error,
    { id: string; filter?: DatasetFilter[] }
  >;
} = {}) => {
  return useMutation({
    mutationFn: ({ id, filter, h3Configuration }: GetDatasetDataParams) => {
      if (!filter?.length) return postDatasetData(id, [], h3Configuration);

      const filterTransformed = getDeepestAdminFilterFirst(filter);

      const filterMap: DatasetFilterPayload[] = mapDatasetFiltersToPayload(
        filterTransformed,
        timezone
      );
      return postDatasetData(id, filterMap, h3Configuration);
    },
    ...options
  });
};

export const usePostDatasetAnalysis = (
  options?: UseMutationOptions<
    { id: string; message?: string },
    CustomError,
    DatasetAnalysisPayload
  >
) => {
  return useMutation({
    mutationFn: (payload: DatasetAnalysisPayload) =>
      postDatasetAnalysis(payload),
    ...options
  });
};

export const useMutationDatasetMetadata = (
  options?: UseMutationOptions<
    DatasetMetadata,
    Error,
    { id: string; h3Configuration?: H3Configuration | null }
  >
): UseMutationResult<
  DatasetMetadata,
  Error,
  { id: string; h3Configuration?: H3Configuration | null }
> => {
  return useMutation({
    mutationFn: ({ id, h3Configuration }) =>
      getDatasetMetadata(id, h3Configuration),
    ...options
  });
};

export const useMutationPatchDatasetSchema = (
  options?: UseMutationOptions<
    DatasetSchemaPayload,
    Error,
    DatasetSchemaPayload,
    { previousMetadata: DatasetMetadata | undefined }
  >
): UseMutationResult<
  DatasetSchemaPayload,
  Error,
  DatasetSchemaPayload,
  { previousMetadata: DatasetMetadata | undefined }
> => {
  return useMutation({
    mutationFn: (payload: DatasetSchemaPayload) => patchDatasetSchema(payload),
    ...options
  });
};

export default function useBatchGetDatasetMetadata(
  datasetIds: string[],
  options?: Omit<
    UseQueryOptions<DatasetMetadata[], Error, DatasetAttributeSchema[]>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery({
    queryKey: ["dataset-metadata-batch", datasetIds],
    queryFn: async () => {
      if (!datasetIds || datasetIds.length === 0) {
        return [];
      }
      const promises = datasetIds.map((id) => getDatasetMetadata(id));
      return Promise.all(promises);
    },
    enabled: !!datasetIds && datasetIds.length > 0,
    ...options
  });
}

export const useGetDatasetMetadata = (
  id: string,
  options?: UseQueryOptions<DatasetMetadata, Error, DatasetMetadata>
): UseQueryResult<DatasetMetadata, Error> => {
  return useQuery({
    queryKey: ["dataset-metadata", id],
    queryFn: () => getDatasetMetadata(id),
    ...options
  });
};

export const useGetDatasetMetadataQuery = (
  id: string,
  options?: UseQueryOptions<DatasetDetailResponse, Error, DatasetDetailResponse>
) => {
  return useQuery({
    queryKey: ["dataset-metadata", id],
    queryFn: () => getDatasetDetail(removeStartFromUnderscore(id)),
    enabled: !!id,
    ...options
  });
};

export const useMutationPatchDatasetMetadata = (
  options?: UseMutationOptions<
    DatasetMetaDataPayload,
    { message: CustomError },
    DatasetMetaDataPayload
  >
): UseMutationResult<
  DatasetMetaDataPayload,
  { message: CustomError },
  DatasetMetaDataPayload
> => {
  const queryClient = useQueryClient();
  const { onSuccess: onSuccessCallback, ...restOptions } = options || {};

  return useMutation({
    mutationFn: (payload: DatasetMetaDataPayload) =>
      patchDatasetMetadata(payload),
    onSuccess: (data, variables, context) => {
      // Need to invalidate datasets-list query after patching metadata
      queryClient.invalidateQueries({ queryKey: ["datasets-list"] });

      // Then run the onSuccess callback if it exists
      onSuccessCallback?.(data, variables, context);
    },
    ...restOptions
  });
};

export const useMutationDeleteDataset = (
  options?: UseMutationOptions<void, Error, string>
): UseMutationResult<void, Error, string> => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteDataset(id),
    onSuccess: () => {
      // Need to invalidate datasets-list after deleting the dataset
      queryClient.invalidateQueries({ queryKey: ["datasets-list"] });
      showToast({
        type: EToastType.SUCCESS,
        autoClose: 3000,
        message: (
          <div>
            <h1 className="text-txtHeading font-bold">
              {MESSAGE.DATASET_DELETE_SUCCESS}
            </h1>
            <span className="text-sm text-txtHeading">
              We’ve deleted the dataset as requested.
            </span>
          </div>
        )
      });
      navigate("/dataset-management");
    },
    onError: () => {
      showToast({
        type: EToastType.ERROR,
        message: MESSAGE.DELETE_DATASET_FAILED
      });
    },
    retry: 0,
    ...options
  });
};

export const useGetAttributeValues = (
  params: UseGetAttributeValuesParams,
  options?: UseQueryOptions<FilterOption[], Error, FilterOption[]>
): UseQueryResult<FilterOption[], Error> => {
  const {
    datasetId,
    selectedFilter: attribute,
    selectedEditFilter: editAttribute,
    isEditMode,
    savedFilters,
    isMultipleSelection,
    timezone
  } = params;

  const attributeToFetch = attribute?.paired_key_attribute_id
    ? {
        ...attribute,
        id: attribute.paired_key_attribute_id,
        value: attribute.paired_key_attribute_id
      }
    : attribute;

  return useQuery({
    queryKey: [
      "dataset-attribute-value",
      datasetId,
      attributeToFetch,
      editAttribute,
      isEditMode
    ],
    queryFn: async () => {
      let filters: DatasetFilter[] = savedFilters || [];

      if (attributeToFetch?.data_type === EAttributeType.BOOLEAN) {
        return [
          { id: "1", label: "True", value: "true" },
          { id: "2", label: "False", value: "false" }
        ];
      }

      // If on edit mode, remove the current edit attribute from the filters to avoid self-filtering
      if (editAttribute)
        filters = removeFilterForAttribute(filters, editAttribute);
      if (attributeToFetch)
        filters = removeFilterForAttribute(filters, attributeToFetch);

      const filterMap: DatasetFilterPayload[] = mapDatasetFiltersToPayload(
        filters,
        timezone
      );

      const response: DatasetAttributeValue[] =
        await getAttributeValuesByAttributeId(
          datasetId,
          attributeToFetch,
          filterMap
        );
      return response?.map((item, index) => ({
        id: `${item.value}-${index}`,
        label: item.display_name,
        value: item.value
      }));
    },
    enabled: !!datasetId && !!attributeToFetch && isMultipleSelection,
    staleTime: 0,
    ...options
  });
};

export const useTranslateDatasetGeometry = (
  id: string,
  options?: UseMutationOptions<
    TranslateDatasetGeometryResponse,
    { message: CustomError<TranslateDatasetGeometryErrorResponse> },
    TranslateDatasetGeometryPayload
  >
) => {
  return useMutation({
    mutationFn: (payload: TranslateDatasetGeometryPayload) =>
      translateDatasetGeometry(id, payload),
    ...options
  });
};

export const useDatasetRemoveNulls = (
  id: string,
  options?: UseMutationOptions<
    TranslateDatasetGeometryResponse,
    Error,
    { attribute_id: string }
  >
) => {
  return useMutation({
    mutationFn: (payload: { attribute_id: string }) =>
      datasetRemoveNulls(id, payload),
    ...options
  });
};

export const useGetDatasetByIdSchema = (
  { datasetId, geometryType }: { datasetId?: string; geometryType: string },
  options?: UseQueryOptions<DatasetSchemaPayload, Error, DatasetSchemas>
): UseQueryResult<DatasetSchemas, Error> => {
  return useQuery({
    queryKey: ["dataset", datasetId, "schema", geometryType], // Include geometryType in key
    queryFn: () => getDatasetByIdSchema(datasetId, geometryType),
    enabled: !!datasetId,
    ...options
  });
};

export const useGetSpatialAggregationTypes = (
  options?: Omit<
    UseQueryOptions<
      SpatialAggregationTypeItem[],
      Error,
      SpatialAggregationTypeItem[]
    >,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: ["spatial-aggregation-types"],
    queryFn: () => getSpatialAggregationTypes(),
    ...options
  });
};

export const useGetDataProviders = (
  options?: Omit<
    UseQueryOptions<DataProviderItem[], Error, DataProviderItem[]>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: ["data-providers"],
    queryFn: () => getDataProviders(),
    ...options
  });
};

export const useDeleteRowsDataset = (
  datasetId: string,
  options?: UseMutationOptions<DeleteRowsDatasetResponse, Error, string[]>
) =>
  useMutation<DeleteRowsDatasetResponse, Error, string[]>({
    mutationFn: (referenceIds: string[]) =>
      deleteRowsDataset(datasetId, referenceIds),
    ...options
  });

export const useAddRowsDataset = (
  datasetId: string,
  options?: UseMutationOptions<
    AddUpdateRowsDatasetResponse,
    Error,
    AddUpdateRowsDatasetPayload[]
  >
) =>
  useMutation<
    AddUpdateRowsDatasetResponse,
    Error,
    AddUpdateRowsDatasetPayload[]
  >({
    mutationFn: (data: AddUpdateRowsDatasetPayload[]) =>
      addRowsDataset(datasetId, data),
    ...options
  });

export const useUpdateRowsDataset = (
  datasetId: string,
  options?: UseMutationOptions<
    AddUpdateRowsDatasetResponse,
    Error,
    { rowId: string; data: AddUpdateRowsDatasetPayload[] }
  >
) =>
  useMutation<
    AddUpdateRowsDatasetResponse,
    Error,
    { rowId: string; data: AddUpdateRowsDatasetPayload[] }
  >({
    mutationFn: ({
      rowId,
      data
    }: {
      rowId: string;
      data: AddUpdateRowsDatasetPayload[];
    }) => updateRowDataset(datasetId, rowId, data),
    ...options
  });
